import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './keys';

/**
 * Generic shape for an inbox row, mirroring the API's `InboxItem`. The
 * `meta` bag is category-specific — the rendering code narrows it via the
 * category name.
 */
export interface InboxItem {
  id: string;
  title: string;
  subtitle?: string;
  occurredAt: string;
  href?: string;
  meta?: Record<string, unknown>;
}

export interface InboxCategory {
  count: number;
  items: InboxItem[];
}

export interface InboxSummary {
  totalCount: number;
  eyewitness: InboxCategory;
  pendingPushes: InboxCategory;
  storiesWithMomentum: InboxCategory;
  expiringBoard: InboxCategory;
  flaggedNewsDesk: InboxCategory;
}

/**
 * Polls the editorial inbox every 30s. The endpoint sets a 15s
 * `private, max-age` so back-to-back fetches in the same React Query
 * window get cached-no-op-served. `refetchOnWindowFocus` is also on so a
 * tab return surfaces fresh items immediately.
 */
export function useInbox() {
  return useQuery({
    queryKey: qk.inbox(),
    queryFn: () => api.get<InboxSummary>('/admin/inbox'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}
